// TODO remove duplicates in nodes.ts
import { z } from 'zod';

const nodeKinds = [
    'activeOperator',
    'slice',
    'number',
    'strCast',
    'numCast',
    'text',
    'boolCast',
    'booleanComparison',
    'appletEventEmit',
    'appletEventListen',
    'varRead',
    'varWrite',
    'varWriteMultiple',
    'varAssigned',
    'script',
    'jsonAgg',
    'jsonParse',
    'numClamp',
    'txtFormat',
    'defaultVal',
    'eventSwitch',
    'timer',
    'materialAssembly',
    'materialDisassembly',
    'materialComparison',
    'materialLookup',
    'materialSerialNumber',
    'start',
    'createMaterial',
    'importMaterial',
    'eventDelay',
    'eventThrottle',
    'eventOneShot',
    'comparisonString',
    'comparisonNumber',
    'attributeMaterialLink',
    'performanceDataCollect',
    'workOrder',
    'workInstruction',
    'processCollect',
    'processSolve',
    'logOutOperator',
    'workOrderSetStatus',
    'updateMaterialStatus',
    'nextTab',
    'goToTab',
    'previousTab',
    'functionalSelect',
    'functionalInputs',
    'functionalOutputs',
    'fileRead',
    'fileWrite',
    'flowError',
    'processStatus',
    'workInstructionStatus',
    'boolean',
    'not',
    'pickLightChannel',
    'activatePickLight',
    'deactivatePickLight',
    'tcpConnection',
    'mqttPublish',
    'mqttSubscribe',
    'sparkplugCommander',
    'sparkplugConsumer',
    'ioLink',
    'isduRead',
    'isduWrite',
    'udpConnection',
    'timestamp',
    'openProtocolConnection',
    'torqueToolCommander',
    'torqueToolConsumer',
    'addMaterialNote',
    'getActiveTab',
    'resetVariables',
] as const;

const nodeKindSchema = z.string().refine(
    (val): val is typeof nodeKinds[number] => nodeKinds.includes(val as typeof nodeKinds[number]),
    val => ({ message: `Unrecognized node kind: ${val}` })
) as z.ZodType<typeof nodeKinds[number]>;

export type NodeKind = z.infer<typeof nodeKindSchema>;

export const baseNodeCfgSchema = z.object({
    controlValues: z.record(z.unknown()).optional(),
    id: z.string(),
    kind: nodeKindSchema,
    label: z.string().optional(),
    disabled: z.boolean().default(false).optional(),
    /** provides a constant default value for a socket if there is no connection */
    socketDefaults: z.object({
        inputs: z.record(z.unknown()).optional(),
        outputs: z.record(z.unknown()).optional(),
    }).optional(),
    width: z.number().optional(),
    x: z.number().optional(),
    y: z.number().optional(),
});
export interface BaseNodeCfg extends z.infer<typeof baseNodeCfgSchema> {}

// export type NodeCfg<KIND extends NodeKind> = z.infer<typeof nodeCfgSchemaMap[KIND]>;

export const nodeSocketKindSchema = z.union([ z.literal('string'), z.literal('number'), z.literal('varValue'), z.literal('boolean'), z.literal('workOrderMaterialData'), z.literal('anyData') ]);
export type NodeDataSocketKind = z.infer<typeof nodeSocketKindSchema>;
export type NodeSocketKind = 'event' | NodeDataSocketKind;

type SocketCategory = 'control' | 'data';
export const socketCategoryMap = {
    anyData: 'data',
    boolean: 'data',
    event: 'control',
    number: 'data',
    string: 'data',
    varValue: 'data',
    workOrderMaterialData: 'data',
} satisfies {
    [kind in NodeSocketKind]: SocketCategory;
};
export type SocketCategoryMap = typeof socketCategoryMap;

export type ControlSocketKind = {
    [K in NodeSocketKind]: SocketCategoryMap[K] extends 'control' ? K : never;
}[NodeSocketKind];

export type DataSocketKind = {
    [K in NodeSocketKind]: SocketCategoryMap[K] extends 'data' ? K : never;
}[NodeSocketKind];

export type NodeSocketDef<CFG extends BaseNodeCfg, ALLOWED_SOCKET_KINDS extends NodeSocketKind> = {
    inputs: (cfg: CFG) => Partial<Record<string, ALLOWED_SOCKET_KINDS>>;
    outputs: (cfg: CFG) => Partial<Record<string, ALLOWED_SOCKET_KINDS>>;
};

export interface NodeSocketCfg<CFG extends BaseNodeCfg> {
    controlSockets: NodeSocketDef<CFG, ControlSocketKind>;
    dataSockets: NodeSocketDef<CFG, DataSocketKind>;
}
